import { Row, Col, Card, Empty } from 'antd';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import { COST_CATEGORIES } from '../FinancePage';
import { BarChartOutlined, PieChartOutlined, RocketOutlined } from '@ant-design/icons';

interface CostsChartsProps {
    overview: any;
    year: number;
    periodLabel: string;
}

const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

const MONTHS_SHORT = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

export function CostsCharts({ overview, year, periodLabel }: CostsChartsProps) {
    if (!overview) return null;

    // Budget vs Actual chart data
    const budgetVsActualData = (overview.budgetVsActual || []).map((m: any) => ({
        name: MONTHS_SHORT[m.month - 1] || m.month,
        Plan: m.planned,
        Actual: m.actual,
    }));

    // Category chart data
    const categoryData = (overview.byCategory || []).slice(0, 6).map((cat: any) => ({
        name: COST_CATEGORIES.find(c => c.value === cat.category)?.label || cat.category,
        value: cat.amount,
    }));

    // Initiative chart data
    const initiativeData = (overview.byInitiative || []).slice(0, 5).map((init: any) => ({
        name: init.name,
        value: init.amount,
    }));

    return (
        <Row gutter={[16, 16]}>
            {/* Budget vs Actual */}
            <Col xs={24} lg={12}>
                <Card title={<span><BarChartOutlined style={{ marginRight: 6 }} />Plan vs Realizacja ({year})</span>} size="small">
                    {budgetVsActualData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={budgetVsActualData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" fontSize={11} />
                                <YAxis fontSize={11} />
                                <Tooltip formatter={(val: number) => `${val.toFixed(0)} PLN`} />
                                <Legend />
                                <Bar dataKey="Plan" fill="#52c41a" name="Plan" />
                                <Bar dataKey="Actual" fill="#1890ff" name="Actual" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <Empty description="Brak danych" />
                    )}
                </Card>
            </Col>

            {/* By Category */}
            <Col xs={24} sm={12} lg={6}>
                <Card title={<span><PieChartOutlined style={{ marginRight: 6 }} />Wg kategorii ({periodLabel})</span>} size="small">
                    {categoryData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={categoryData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={80}
                                    innerRadius={40}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ name, percent }) => `${name.substring(0, 10)}... ${(percent * 100).toFixed(0)}%`}
                                    labelLine={false}
                                >
                                    {categoryData.map((_: any, index: number) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(val: number) => `${val.toFixed(0)} PLN`} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <Empty description="Brak danych" />
                    )}
                </Card>
            </Col>

            {/* By Initiative */}
            <Col xs={24} sm={12} lg={6}>
                <Card title={<span><RocketOutlined style={{ marginRight: 6 }} />Wg inicjatyw ({periodLabel})</span>} size="small">
                    {initiativeData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart
                                layout="vertical"
                                data={initiativeData}
                                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" fontSize={11} />
                                <YAxis dataKey="name" type="category" width={80} fontSize={10} />
                                <Tooltip formatter={(val: number) => `${val.toFixed(0)} PLN`} />
                                <Bar dataKey="value" fill="#722ed1" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <Empty description="Brak inicjatyw" />
                    )}
                </Card>
            </Col>
        </Row>
    );
}
